import { BookOpen, FileText, Image as ImageIcon, Video, HelpCircle, Clock, Award, Users, GraduationCap } from "lucide-react"
import { Button } from "../ui/button"

// Preview pour un chapitre
export function LessonPreview({ lesson, onEdit }) {
  if (!lesson) return null

  const blocksCount = lesson.blocks?.length || 0

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#141835] rounded-lg">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{lesson.title || 'chapitre sans titre'}</h2>
              <p className="text-sm text-gray-600 mt-1">Aperçu de le chapitre</p>
            </div>
          </div>
          {onEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={onEdit}
              className="h-8"
            >
              Modifier
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {lesson.description && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Description</h3>
              <p className="text-gray-700 whitespace-pre-wrap">{lesson.description}</p>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-500">Ordre</label>
                <p className="text-gray-900 font-medium">{lesson.order || 1}</p>
              </div>
              {lesson.duration && (
                <div>
                  <label className="text-sm text-gray-500 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Durée
                  </label>
                  <p className="text-gray-900 font-medium">{lesson.duration} minutes</p>
                </div>
              )}
              <div>
                <label className="text-sm text-gray-500">Blocs</label>
                <p className="text-gray-900 font-medium">{blocksCount} bloc{blocksCount !== 1 ? 's' : ''}</p>
              </div>
            </div>
          </div>

          {blocksCount > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Blocs de la chapitre</h3>
              <div className="space-y-2">
                {lesson.blocks.map((block, index) => (
                  <div key={block.id || index} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      {block.type === 'TEXT' && <FileText className="w-4 h-4 text-gray-500" />}
                      {block.type === 'IMAGE' && <ImageIcon className="w-4 h-4 text-gray-500" />}
                      {block.type === 'VIDEO' && <Video className="w-4 h-4 text-gray-500" />}
                      <span className="text-sm font-medium text-gray-700">{block.title || `Bloc ${index + 1}`}</span>
                      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded ml-auto">
                        {block.type}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Preview pour un Module
export function ModulePreview({ module, onEdit }) {
  if (!module) return null

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#141835] rounded-lg">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{module.title || 'Module sans titre'}</h2>
              <p className="text-sm text-gray-600 mt-1">Aperçu du module</p>
            </div>
          </div>
          {onEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={onEdit}
              className="h-8"
            >
              Modifier
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {module.description && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Description</h3>
              <p className="text-gray-700 whitespace-pre-wrap">{module.description}</p>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-500">Ordre</label>
                <p className="text-gray-900 font-medium">{module.order || 1}</p>
              </div>
              {module.duration && (
                <div>
                  <label className="text-sm text-gray-500 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Durée
                  </label>
                  <p className="text-gray-900 font-medium">{module.duration} minutes</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Preview pour un Bloc
export function BlockPreview({ block, onEdit }) {
  if (!block) return null

  const getBlockIcon = () => {
    switch (block.type) {
      case 'TEXT':
        return FileText
      case 'IMAGE':
        return ImageIcon
      case 'VIDEO':
        return Video
      case 'ASSIGNMENT':
        return HelpCircle
      default:
        return FileText
    }
  }

  const Icon = getBlockIcon()

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#141835] rounded-lg">
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{block.title || 'Bloc sans titre'}</h2>
              <p className="text-sm text-gray-600 mt-1">Aperçu du bloc</p>
            </div>
          </div>
          {onEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={onEdit}
              className="h-8"
            >
              Modifier
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {block.description && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Description</h3>
              <p className="text-gray-700 whitespace-pre-wrap">{block.description}</p>
            </div>
          )}

          {/* Contenu selon le type */}
          {block.type === 'TEXT' && block.content && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Contenu</h3>
              <div 
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ 
                  __html: typeof block.content === 'string' 
                    ? block.content 
                    : block.content?.html || '' 
                }} 
              />
            </div>
          )}

          {block.type === 'IMAGE' && block.content && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Image</h3>
              {block.content.imageUrl && (
                <div className="mt-4">
                  <img 
                    src={block.content.imageUrl} 
                    alt={block.content.alt || block.title}
                    className="max-w-full h-auto rounded-lg"
                  />
                  {block.content.caption && (
                    <p className="text-sm text-gray-600 mt-2 italic">{block.content.caption}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {block.type === 'VIDEO' && block.content && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Vidéo</h3>
              {block.content.videoUrl && (
                <div className="mt-4">
                  <video 
                    src={block.content.videoUrl} 
                    controls
                    className="w-full rounded-lg"
                  />
                  {block.content.transcript && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Transcription</h4>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">{block.content.transcript}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-500">Type</label>
                <p className="text-gray-900 font-medium">{block.type}</p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Ordre</label>
                <p className="text-gray-900 font-medium">{block.order || 1}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Preview pour un Quiz
export function QuizPreview({ quiz, onEdit }) {
  if (!quiz) return null

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#141835] rounded-lg">
              <HelpCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{quiz.title || 'Quiz sans titre'}</h2>
              <p className="text-sm text-gray-600 mt-1">Aperçu du quiz</p>
            </div>
          </div>
          {onEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={onEdit}
              className="h-8"
            >
              Modifier
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {quiz.description && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Description</h3>
              <p className="text-gray-700 whitespace-pre-wrap">{quiz.description}</p>
            </div>
          )}

          {quiz.instructions && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Instructions</h3>
              <p className="text-gray-700 whitespace-pre-wrap">{quiz.instructions}</p>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Paramètres</h3>
            <div className="grid grid-cols-2 gap-4">
              {quiz.timeLimit && (
                <div>
                  <label className="text-sm text-gray-500 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Limite de temps
                  </label>
                  <p className="text-gray-900 font-medium">{quiz.timeLimit} minutes</p>
                </div>
              )}
              {quiz.passingScore !== undefined && (
                <div>
                  <label className="text-sm text-gray-500 flex items-center gap-1">
                    <Award className="w-4 h-4" />
                    Score de passage
                  </label>
                  <p className="text-gray-900 font-medium">{quiz.passingScore}%</p>
                </div>
              )}
              {quiz.maxAttempts && (
                <div>
                  <label className="text-sm text-gray-500">Tentatives maximum</label>
                  <p className="text-gray-900 font-medium">{quiz.maxAttempts}</p>
                </div>
              )}
            </div>
          </div>

          {quiz.questions && quiz.questions.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Questions ({quiz.questions.length})</h3>
              <div className="space-y-3">
                {quiz.questions.map((question, index) => (
                  <div key={question.id || index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-medium text-gray-700">Question {index + 1}</span>
                          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                            {question.type || 'SINGLE_CHOICE'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {question.points || 1} point{question.points > 1 ? 's' : ''}
                          </span>
                        </div>
                        <p className="text-gray-900 font-medium">{question.question || question.questionText}</p>
                        {question.options && question.options.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {question.options.map((option, optIndex) => (
                              <div key={optIndex} className="flex items-center gap-2 text-sm">
                                <input
                                  type={question.type === 'MULTIPLE_CHOICE' ? 'checkbox' : 'radio'}
                                  checked={option.isCorrect}
                                  readOnly
                                  className="text-green-600"
                                />
                                <span className={option.isCorrect ? 'text-green-700 font-medium' : 'text-gray-600'}>
                                  {option.text}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Preview pour un Course
export function CoursePreview({ course, onEdit }) {
  if (!course) return null

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#141835] rounded-lg">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{course.title || 'Cours sans titre'}</h2>
              <p className="text-sm text-gray-600 mt-1">Aperçu du cours</p>
            </div>
          </div>
          {onEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={onEdit}
              className="h-8"
            >
              Modifier
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {course.shortDescription && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Description courte</h3>
              <p className="text-gray-700">{course.shortDescription}</p>
            </div>
          )}

          {course.description && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Description complète</h3>
              <p className="text-gray-700 whitespace-pre-wrap">{course.description}</p>
            </div>
          )}

          {course.requirements && course.requirements.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Prérequis</h3>
              <ul className="list-disc list-inside space-y-1">
                {course.requirements.map((req, index) => (
                  <li key={index} className="text-gray-700">{req}</li>
                ))}
              </ul>
            </div>
          )}

          {course.objectives && course.objectives.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Objectifs</h3>
              <ul className="list-disc list-inside space-y-1">
                {course.objectives.map((obj, index) => (
                  <li key={index} className="text-gray-700">{obj}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

